<script>
import { PageHeaderStyle } from "./style";
import VueTypes from "vue-types";
import { defineComponent, computed } from "vue";
import { useRoute } from "vue-router";

/**
 * Route-based title & breadcrumb mapping
 * Key: route name (from AdminRoutes.js)
 * Value: { title, breadcrumb[] }
 */
const routeConfig = {
  // ─── Manage ───
  manage: { title: 'จัดการบัญชี', breadcrumb: ['จัดการบัญชี'] },

  // ─── FlowAccount ───
  flowaccount_dashboard: { title: 'FlowAccount — Dashboard', breadcrumb: ['FlowAccount', 'Dashboard'] },
  flowaccount_connection: { title: 'FlowAccount — การเชื่อมต่อ', breadcrumb: ['FlowAccount', 'การเชื่อมต่อ'] },
  flowaccount_hubspot: { title: 'FlowAccount — HubSpot', breadcrumb: ['FlowAccount', 'HubSpot'] },

  // ─── PeakAccount ───
  peakaccount_dashboard: { title: 'PeakAccount — Dashboard', breadcrumb: ['PeakAccount', 'Dashboard'] },
  peakaccount_connection: { title: 'PeakAccount — การเชื่อมต่อ', breadcrumb: ['PeakAccount', 'การเชื่อมต่อ'] },
  peakaccount_hubspot: { title: 'PeakAccount — HubSpot', breadcrumb: ['PeakAccount', 'HubSpot'] },

  // ─── ThaiBulkSMS ───
  thaibulksms_dashboard: { title: 'ThaiBulkSMS — Dashboard', breadcrumb: ['ThaiBulkSMS', 'Dashboard'] },
  thaibulksms_connection: { title: 'ThaiBulkSMS — การเชื่อมต่อ', breadcrumb: ['ThaiBulkSMS', 'การเชื่อมต่อ'] },
  thaibulksms_hubspot: { title: 'ThaiBulkSMS — HubSpot', breadcrumb: ['ThaiBulkSMS', 'HubSpot'] },
  thaibulksms_phone_number_template: { title: 'ThaiBulkSMS — Template เบอร์โทร', breadcrumb: ['ThaiBulkSMS', 'Template เบอร์โทร'] },
  thaibulksms_message_template: { title: 'ThaiBulkSMS — Template ข้อความ', breadcrumb: ['ThaiBulkSMS', 'Template ข้อความ'] },
  thaibulksms_url_management: { title: 'ThaiBulkSMS — จัดการ URL', breadcrumb: ['ThaiBulkSMS', 'จัดการ URL'] },

  // ─── SmartComm ───
  smartcomm_dashboard: { title: 'SmartComm — Dashboard', breadcrumb: ['SmartComm', 'Dashboard'] },
  smartcomm_connection: { title: 'SmartComm — การเชื่อมต่อ', breadcrumb: ['SmartComm', 'การเชื่อมต่อ'] },
  smartcomm_hubspot: { title: 'SmartComm — HubSpot', breadcrumb: ['SmartComm', 'HubSpot'] },
  smartcomm_phone_number_template: { title: 'SmartComm — Template เบอร์โทร', breadcrumb: ['SmartComm', 'Template เบอร์โทร'] },
  smartcomm_message_template: { title: 'SmartComm — Template ข้อความ', breadcrumb: ['SmartComm', 'Template ข้อความ'] },
};

/**
 * Fallback: derive title from route path segments
 * e.g. "/flowaccount/dashboard" → "Flowaccount — Dashboard"
 */
const deriveTitleFromPath = (path) => {
  const segments = path.replace(/^\/+/, '').split('/').filter(Boolean);
  if (segments.length === 0) return 'Dashboard';
  return segments.map(s => s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ')).join(' — ');
};

const deriveBreadcrumbFromPath = (path) => {
  const segments = path.replace(/^\/+/, '').split('/').filter(Boolean);
  return segments.map(s => s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' '));
};

export const PageHeader = defineComponent({
  components: {
    PageHeaderStyle,
  },
  props: {
    title: VueTypes.oneOfType([VueTypes.string, VueTypes.object]),
    subTitle: VueTypes.oneOfType([VueTypes.string, VueTypes.object]),
    bgColor: VueTypes.string,
    class: VueTypes.string,
    routes: VueTypes.array.def([]),
    buttons: VueTypes.array,
    ghost: VueTypes.bool,
    breadcrumb: VueTypes.object,
  },

  setup(props) {
    const route = useRoute();

    const resolvedTitle = computed(() => {
      // If title prop is explicitly provided, use it
      if (props.title) return props.title;

      // Try route name mapping
      const config = routeConfig[route.name];
      if (config) return config.title;

      // Fallback: derive from path
      return deriveTitleFromPath(route.path);
    });

    const resolvedRoutes = computed(() => {
      // If routes prop is explicitly provided and not empty, use it
      if (props.routes && props.routes.length > 0) return props.routes;

      // Build breadcrumb from config
      const config = routeConfig[route.name];
      const crumbs = config ? config.breadcrumb : deriveBreadcrumbFromPath(route.path);

      // Always start with Home
      const result = [{ path: '/manage', breadcrumbName: 'Dashboard' }];
      crumbs.forEach((name, idx) => {
        result.push({
          path: idx < crumbs.length - 1 ? '#' : route.path,
          breadcrumbName: name,
        });
      });

      return result;
    });

    return { resolvedTitle, resolvedRoutes };
  },

  render() {
    return (
      <div>
        <div>
          <PageHeaderStyle
            class={this.class}
            title={this.resolvedTitle || (this.$slots.title && this.$slots.title())}
            subTitle={
              this.subTitle ||
              (this.$slots.subTitle ? this.$slots.subTitle() : null)
            }
            ghost={this.ghost}
            extra={this.$slots.buttons && this.$slots.buttons()}
          >
            {this.resolvedRoutes.length ? (
              <a-breadcrumb>
                {this.resolvedRoutes.map((item, key) => {
                  return (
                    <a-breadcrumb-item key={key}>
                      {key == 0 && <unicon name="estate"></unicon>}
                      {key !== this.resolvedRoutes.length - 1 ? (
                        <a to="#">{item.breadcrumbName}</a>
                      ) : (
                        item.breadcrumbName
                      )}
                    </a-breadcrumb-item>
                  );
                })}
              </a-breadcrumb>
            ) : null}
          </PageHeaderStyle>
        </div>
      </div>
    );
  },
});

export default PageHeader;
</script>
